import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'thankYouMessage'
	| 'provideAdditionalFeedback'
	| 'optInQuestion'
	| 'yesOptIn'
	| 'additionalFeedbackButton'
	| 'optedInMessage'
	| 'dismissBanner'
	| 'whatWorkedQuestion'
	| 'whatDidntWorkQuestion'
	| 'itWasLabel'
	| 'categoryUpToDate'
	| 'categoryAccurate'
	| 'categoryHelpful'
	| 'categoryOnBrief'
	| 'categoryWellSourced'
	| 'categoryOther'
	| 'categoryOutOfDate'
	| 'categoryInaccurate'
	| 'categoryTooLong'
	| 'categoryTooShort'
	| 'categoryPoorlySourced'
	| 'categoryInappropriate'
	| 'categoryGoodPlan'
	| 'categoryAskedGoodQuestions'
	| 'categoryReasonableAssumptions'
	| 'categoryShowedMeSomethingNew'
	| 'categoryTooManyQuestions'
	| 'categoryNotEnoughQuestions'
	| 'categoryIncorrectPlan'
	| 'categoryBuildFailed'
	| 'categoryTooWordy'
	| 'categoryBadAssumptions'
	| 'additionalFeedbackLabel'
	| 'additionalFeedbackPlaceholder'
	| 'includeMessagesCheckbox'
	| 'enrollInResearchCheckbox'
	| 'privacyPolicyLink'
	| 'submitFeedback'
	| 'additionalFeedbackRequired'
	| 'feedbackPrivacyDisclaimer';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		thankYouMessage: {
			id: 'ai-mate.feedback-confirmation-banner.thank-you-message',
			defaultMessage: 'Thank you for your feedback!',
			description: 'Message shown when feedback has been recorded',
		},
		provideAdditionalFeedback: {
			id: 'ai-mate.feedback-confirmation-banner.provide-additional-feedback',
			defaultMessage: 'Provide additional feedback',
			description: 'Button label to open the full feedback form',
		},
		optInQuestion: {
			id: 'ai-mate.feedback-confirmation-banner.opt-in-question',
			defaultMessage:
				'Do you want to <b>include recent messages from this conversation</b> to help Atlassian debug issues? Atlassian may contact you in order to debug the reported issues.',
			description:
				'Question shown when user has record conversation history unchecked, asking if they want to opt in',
		},
		yesOptIn: {
			id: 'ai-mate.feedback-confirmation-banner.yes-opt-in',
			defaultMessage: 'Yes, opt in',
			description: 'Button to opt in to including conversation messages for debugging',
		},
		additionalFeedbackButton: {
			id: 'ai-mate.feedback-confirmation-banner.additional-feedback-button',
			defaultMessage: 'Additional feedback',
			description: 'Button label for additional feedback option in opt-in modal',
		},
		optedInMessage: {
			id: 'ai-mate.feedback-confirmation-banner.opted-in-message',
			defaultMessage: 'Thanks for opting in to share feedback!',
			description: 'Message shown after user opts in to include conversation messages',
		},
		dismissBanner: {
			id: 'ai-mate.feedback-confirmation-banner.dismiss-banner',
			defaultMessage: 'Dismiss',
			description: 'Label for the button to dismiss the feedback confirmation banner',
		},
		whatWorkedQuestion: {
			id: 'ai-mate.feedback-confirmation-banner.what-worked-question',
			defaultMessage: 'What worked about this response?',
			description: 'Question shown for positive feedback',
		},
		whatDidntWorkQuestion: {
			id: 'ai-mate.feedback-confirmation-banner.what-didnt-work-question',
			defaultMessage: "What didn't work about this response?",
			description: 'Question shown for negative feedback',
		},
		itWasLabel: {
			id: 'ai-mate.feedback-confirmation-banner.it-was-label',
			defaultMessage: 'It was...',
			description: 'Label text displayed above feedback category selection buttons to prompt users',
		},
		categoryUpToDate: {
			id: 'ai-mate.feedback-confirmation-banner.category-up-to-date',
			defaultMessage: 'Up to date',
			description: 'Category option for positive feedback',
		},
		categoryAccurate: {
			id: 'ai-mate.feedback-confirmation-banner.category-accurate',
			defaultMessage: 'Accurate',
			description: 'Category option for positive feedback',
		},
		categoryHelpful: {
			id: 'ai-mate.feedback-confirmation-banner.category-helpful',
			defaultMessage: 'Helpful',
			description: 'Category option for positive feedback',
		},
		categoryOnBrief: {
			id: 'ai-mate.feedback-confirmation-banner.category-on-brief',
			defaultMessage: 'On-brief',
			description: 'Category option for positive feedback',
		},
		categoryWellSourced: {
			id: 'ai-mate.feedback-confirmation-banner.category-well-sourced',
			defaultMessage: 'Well-sourced',
			description: 'Category option for positive feedback',
		},
		categoryOther: {
			id: 'ai-mate.feedback-confirmation-banner.category-other',
			defaultMessage: 'Other',
			description: 'Category option for both positive and negative feedback',
		},
		categoryOutOfDate: {
			id: 'ai-mate.feedback-confirmation-banner.category-out-of-date',
			defaultMessage: 'Out of date',
			description: 'Category option for negative feedback',
		},
		categoryInaccurate: {
			id: 'ai-mate.feedback-confirmation-banner.category-inaccurate',
			defaultMessage: 'Inaccurate',
			description: 'Category option for negative feedback',
		},
		categoryTooLong: {
			id: 'ai-mate.feedback-confirmation-banner.category-too-long',
			defaultMessage: 'Too long',
			description: 'Category option for negative feedback',
		},
		categoryTooShort: {
			id: 'ai-mate.feedback-confirmation-banner.category-too-short',
			defaultMessage: 'Too short',
			description: 'Category option for negative feedback',
		},
		categoryPoorlySourced: {
			id: 'ai-mate.feedback-confirmation-banner.category-poorly-sourced',
			defaultMessage: 'Poorly-sourced',
			description: 'Category option for negative feedback',
		},
		categoryInappropriate: {
			id: 'ai-mate.feedback-confirmation-banner.category-inappropriate',
			defaultMessage: 'Inappropriate or harmful',
			description: 'Category option for negative feedback',
		},
		categoryGoodPlan: {
			id: 'ai-mate.feedback-confirmation-banner.category-good-plan',
			defaultMessage: 'Good plan',
			description: 'Studio category option for positive feedback',
		},
		categoryAskedGoodQuestions: {
			id: 'ai-mate.feedback-confirmation-banner.category-asked-good-questions',
			defaultMessage: 'Asking good questions',
			description: 'Studio category option for positive feedback',
		},
		categoryReasonableAssumptions: {
			id: 'ai-mate.feedback-confirmation-banner.category-reasonable-assumptions',
			defaultMessage: 'Making good assumptions',
			description: 'Studio category option for positive feedback',
		},
		categoryShowedMeSomethingNew: {
			id: 'ai-mate.feedback-confirmation-banner.category-showed-me-something-new',
			defaultMessage: 'Educational',
			description: 'Studio category option for positive feedback',
		},
		categoryTooManyQuestions: {
			id: 'ai-mate.feedback-confirmation-banner.category-too-many-questions',
			defaultMessage: 'Asking lots of questions',
			description: 'Studio category option for negative feedback',
		},
		categoryNotEnoughQuestions: {
			id: 'ai-mate.feedback-confirmation-banner.category-not-enough-questions',
			defaultMessage: 'Not enough questions',
			description: 'Studio category option for negative feedback',
		},
		categoryIncorrectPlan: {
			id: 'ai-mate.feedback-confirmation-banner.category-incorrect-plan',
			defaultMessage: 'Incorrect',
			description: 'Studio category option for negative feedback',
		},
		categoryBuildFailed: {
			id: 'ai-mate.feedback-confirmation-banner.category-build-failed',
			defaultMessage: 'Not working (build failed)',
			description: 'Studio category option for negative feedback',
		},
		categoryTooWordy: {
			id: 'ai-mate.feedback-confirmation-banner.category-too-wordy',
			defaultMessage: 'Too wordy',
			description: 'Studio category option for negative feedback',
		},
		categoryBadAssumptions: {
			id: 'ai-mate.feedback-confirmation-banner.category-bad-assumptions',
			defaultMessage: 'Making bad assumptions',
			description: 'Studio category option for negative feedback',
		},
		additionalFeedbackLabel: {
			id: 'ai-mate.feedback-confirmation-banner.additional-feedback-label',
			defaultMessage: 'Additional feedback',
			description: 'Label for additional feedback text area',
		},
		additionalFeedbackPlaceholder: {
			id: 'ai-mate.feedback-confirmation-banner.additional-feedback-placeholder',
			defaultMessage: 'Tell us more',
			description: 'Placeholder for additional feedback text area',
		},
		includeMessagesCheckbox: {
			id: 'ai-mate.feedback-confirmation-banner.include-messages-checkbox',
			defaultMessage:
				'Yes, include recent messages from this conversation to help Atlassian debug issues. Atlassian may contact me in order to debug the reported issue.',
			description:
				'Label for checkbox to include user input and consent to being contacted for debugging',
		},
		enrollInResearchCheckbox: {
			id: 'ai-mate.feedback-confirmation-banner.enroll-in-research-checkbox',
			defaultMessage:
				'Yes, Atlassian may contact me to learn more about my experience and improve Atlassian apps and services. I acknowledge the {privacyPolicyLink}.',
			description: 'Label for checkbox to consent to being enrolled in research',
		},
		privacyPolicyLink: {
			id: 'ai-mate.feedback-confirmation-banner.privacy-policy-link',
			defaultMessage: 'Atlassian Privacy Policy',
			description: 'Link text that directs users to the Atlassian Privacy Policy page',
		},
		submitFeedback: {
			id: 'ai-mate.feedback-confirmation-banner.submit-feedback',
			defaultMessage: 'Submit feedback',
			description: 'Button label to submit feedback',
		},
		additionalFeedbackRequired: {
			id: 'ai-mate.feedback-confirmation-banner.additional-feedback-required',
			defaultMessage: 'Please provide additional details when selecting "Other"',
			description:
				'Error message shown when user selects "Other" category without providing details',
		},
		feedbackPrivacyDisclaimer: {
			id: 'ai-mate.feedback-confirmation-banner.feedback-privacy-disclaimer',
			defaultMessage:
				'Your feedback will be visible to agent owners and admins in studio for up to 28 days.',
			description:
				'Privacy disclaimer informing users that their feedback may be reviewed by agent administrators',
		},
	});

export default message;
